import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// ─── Flotte ───────────────────────────────────────────────────────────────────
const MAX_MONO = 2;
const MAX_BI   = 2;

// ─── TZ Tahiti ────────────────────────────────────────────────────────────────
const getTahitiToday = () =>
  new Date().toLocaleDateString('fr-CA', { timeZone: 'Pacific/Tahiti' });

const buildDateStr = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

// ─── Calcul disponibilités (uniquement réservations acceptées/payées) ─────────
// Les demandes "pending" ne bloquent PAS le calendrier public
const computeAvailability = (reservations) => {
  const used = { matin: { mono: 0, bi: 0 }, apresmidi: { mono: 0, bi: 0 } };

  reservations.forEach((res) => {
    // Fermeture manuelle
    if ((res.monoplace_qty ?? 0) >= 99 || (res.biplace_qty ?? 0) >= 99) {
      used.matin.mono     = MAX_MONO; used.matin.bi       = MAX_BI;
      used.apresmidi.mono = MAX_MONO; used.apresmidi.bi   = MAX_BI;
      return;
    }

    const mono = res.monoplace_qty ?? Math.max(0, (res.number_of_people || 0) % 2);
    const bi   = res.biplace_qty   ?? Math.floor((res.number_of_people || 0) / 2);

    // Lire depuis colonne dédiée, sinon parser les notes
    let slot = res.time_slot || null;
    if (!slot) {
      const m = (res.notes || '').match(/Cr.neau\s*:\s*(matin|apresmidi|journee)/i);
      slot = m ? m[1].toLowerCase() : 'journee';
    }

    if (slot === 'matin' || slot === 'journee') {
      used.matin.mono += mono; used.matin.bi += bi;
    }
    if (slot === 'apresmidi' || slot === 'journee') {
      used.apresmidi.mono += mono; used.apresmidi.bi += bi;
    }
  });

  return {
    matin:     { mono: Math.max(0, MAX_MONO - used.matin.mono),     bi: Math.max(0, MAX_BI - used.matin.bi) },
    apresmidi: { mono: Math.max(0, MAX_MONO - used.apresmidi.mono), bi: Math.max(0, MAX_BI - used.apresmidi.bi) },
    journee: {
      mono: Math.min(Math.max(0, MAX_MONO - used.matin.mono), Math.max(0, MAX_MONO - used.apresmidi.mono)),
      bi:   Math.min(Math.max(0, MAX_BI   - used.matin.bi),   Math.max(0, MAX_BI   - used.apresmidi.bi)),
    },
  };
};

const isFull = (d) => d.mono === 0 && d.bi === 0;

// ─── Ligne créneau dans la case ──────────────────────────────────────────────
const SlotRow = ({ label, d, isMobile }) => {
  const full    = isFull(d);
  const partial = !full && (d.mono < MAX_MONO || d.bi < MAX_BI);

  const bg   = full ? 'bg-red-50'     : partial ? 'bg-amber-50'    : 'bg-emerald-50';
  const text = full ? 'text-red-500'  : partial ? 'text-amber-700' : 'text-emerald-700';

  if (isMobile) {
    return (
      <div className={`rounded p-1.5 ${bg} flex flex-col items-center text-center justify-center h-full`}>
        <span className="text-[10px] font-bold text-slate-500 mb-1">{label}</span>
        {full ? (
          <span className={`text-[10px] font-black ${text}`}>Complet</span>
        ) : (
          <div className="text-[10px] font-semibold text-slate-700 flex flex-col leading-tight gap-0.5">
            {d.bi > 0 && <span>{d.bi} Bi</span>}
            {d.mono > 0 && <span>{d.mono} Mono</span>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded px-1.5 py-0.5 ${bg} flex items-center justify-between gap-1`}>
      <span className="text-[9px] xl:text-[10px] font-bold text-slate-500 shrink-0 w-[42px] xl:w-[48px]">{label}</span>
      {full ? (
        <span className={`text-[9px] xl:text-[10px] font-black ${text}`}>Complet</span>
      ) : (
        <span className="text-[9px] xl:text-[10px] font-semibold text-slate-700 text-right leading-tight">
          {d.bi > 0 && <span>{d.bi} Bi</span>}
          {d.bi > 0 && d.mono > 0 && ' · '}
          {d.mono > 0 && <span>{d.mono} Mono</span>}
        </span>
      )}
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────
const AvailabilityCalendar = () => {
  const navigate = useNavigate();
  const today    = getTahitiToday();

  const [currentYM, setCurrentYM] = useState(() => {
    const [y, m] = today.split('-');
    return { year: parseInt(y, 10), month: parseInt(m, 10) - 1 };
  });
  const [resByDate, setResByDate] = useState({});
  const [loading, setLoading]     = useState(true);

  const { year, month } = currentYM;

  const fetchData = useCallback(async () => {
    setLoading(true);
    const start = buildDateStr(year, month, 1);
    const end   = buildDateStr(year, month, new Date(year, month + 1, 0).getDate());

    // ⚠️ UNIQUEMENT les réservations acceptées ou payées bloquent le calendrier
    // On utilise la fonction Edge pour contourner la RLS des utilisateurs non connectés
    const { data, error } = await supabase.functions.invoke('get-availability', {
      body: { start, end }
    });

    console.log("Supabase edge function fetch in AvailabilityCalendar:", { data, error });

    const grouped = (data || []).reduce((acc, r) => {
      if (!r.reservation_date) return acc;
      if (!acc[r.reservation_date]) acc[r.reservation_date] = [];
      acc[r.reservation_date].push(r);
      return acc;
    }, {});
    setResByDate(grouped);
    setLoading(false);
  }, [year, month]);

  useEffect(() => {
    fetchData();

    // Recharger si l'onglet redevient actif
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Écoute temps réel pour synchroniser les créneaux immédiatement lors d'une validation admin
    const channel = supabase
      .channel('public-availability-sync')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'reservations' },
        () => {
          fetchData();
        }
      )
      .subscribe();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      supabase.removeChannel(channel);
    };
  }, [fetchData]);

  const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  const DAYS_FR   = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];
  const firstDow    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setCurrentYM(({ year: y, month: m }) => m === 0 ? { year: y-1, month: 11 } : { year: y, month: m-1 });
  const nextMonth = () => setCurrentYM(({ year: y, month: m }) => m === 11 ? { year: y+1, month: 0 } : { year: y, month: m+1 });

  const handleClick = (dateStr, blocked, past) => {
    if (blocked || past) return;
    navigate(`/location-de-kayak/?date=${dateStr}`);
  };

  return (
    <div className="w-full bg-white rounded-3xl shadow-sm border border-slate-100">
      {/* En-tête */}
      <div className="flex items-center justify-between px-4 sm:px-8 py-4 sm:py-6 border-b border-slate-100">
        <button onClick={prevMonth} className="p-2 sm:p-3 rounded-xl hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-800" aria-label="Mois précédent">
          <ChevronLeft size={22} />
        </button>
        <div className="text-center">
          <h3 className="text-xl sm:text-3xl font-black text-slate-800 tracking-tight">{MONTHS_FR[month]} {year}</h3>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Flotte : 2 Kayaks Biplaces · 2 Kayaks Monoplaces</p>
        </div>
        <button onClick={nextMonth} className="p-2 sm:p-3 rounded-xl hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-800" aria-label="Mois suivant">
          <ChevronRight size={22} />
        </button>
      </div>

      {/* Légende créneaux */}
      <div className="flex flex-wrap justify-center gap-3 sm:gap-6 px-4 py-3 bg-slate-50 border-b border-slate-100 text-xs sm:text-sm text-slate-600">
        <span className="font-semibold text-slate-500">Créneaux :</span>
        <span><span className="font-bold text-cyan-700">Mat</span> = Matin (08h-11h30)</span>
        <span><span className="font-bold text-cyan-700">Apr</span> = Après-midi (13h30-17h)</span>
        <span><span className="font-bold text-cyan-700">Jrn</span> = Journée (08h-17h)</span>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-24">
          <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="p-2 sm:p-4">
          
          {/* VUE MOBILE : Liste verticale */}
          <div className="block lg:hidden space-y-3">
             <div className="text-center text-xs text-slate-500 mb-3 italic">
               Affichage des disponibilités à partir d'aujourd'hui
             </div>
             {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                const dateStr = buildDateStr(year, month, day);
                const isPast  = dateStr < today;
                
                // On cache les jours passés sur la vue mobile pour un meilleur confort
                if (isPast) return null;
                
                const isToday = dateStr === today;
                const dayRes  = resByDate[dateStr] || [];
                const dispo   = computeAvailability(dayRes);
                const allBlocked = isFull(dispo.matin) && isFull(dispo.apresmidi);
                
                let cardClass = 'bg-white border-slate-200';
                if (allBlocked) cardClass = 'bg-red-50 border-red-200';
                else if (isToday) cardClass = 'border-cyan-400 ring-1 ring-cyan-400';

                const dayOfWeekStr = DAYS_FR[new Date(year, month, day).getDay()];

                return (
                  <div 
                    key={day}
                    onClick={() => handleClick(dateStr, allBlocked, isPast)}
                    className={`border rounded-xl p-3 flex flex-col gap-2 shadow-sm transition-shadow ${cardClass} ${allBlocked ? 'cursor-not-allowed opacity-80' : 'cursor-pointer hover:shadow-md'}`}
                  >
                    <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                      <div className="font-bold text-slate-800 text-[15px]">
                        {dayOfWeekStr} {day} {MONTHS_FR[month]}
                      </div>
                      {isToday && <span className="bg-cyan-100 text-cyan-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Aujourd'hui</span>}
                    </div>
                    {allBlocked ? (
                       <div className="text-sm font-bold text-red-500 text-center py-4">Complet pour cette journée</div>
                    ) : (
                       <div className="grid grid-cols-3 gap-2">
                          <SlotRow label="Matin" d={dispo.matin} isMobile={true} />
                          <SlotRow label="Apr-M" d={dispo.apresmidi} isMobile={true} />
                          <SlotRow label="Journée" d={dispo.journee} isMobile={true} />
                       </div>
                    )}
                  </div>
                );
             })}
          </div>

          {/* VUE DESKTOP : Grille */}
          <div className="hidden lg:block overflow-x-auto">
            <div className="min-w-[850px]">
              <div className="grid grid-cols-7 mb-2">
              {DAYS_FR.map((d) => (
                <div key={d} className="text-center text-xs font-bold text-slate-500 uppercase py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: firstDow }).map((_, i) => <div key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                const dateStr = buildDateStr(year, month, day);
                const isPast  = dateStr < today;
                const isToday = dateStr === today;
                const dayRes  = resByDate[dateStr] || [];
                const dispo   = computeAvailability(dayRes);
                const allBlocked = isFull(dispo.matin) && isFull(dispo.apresmidi);

                let cellClass   = 'bg-white border-slate-100';
                let numberClass = 'text-slate-800';
                let cursor      = 'cursor-pointer hover:shadow-md hover:border-cyan-300';

                if (isPast) return <div key={day} className="p-2 min-h-[130px]" />;
                if (allBlocked) { cellClass = 'bg-red-50 border-red-200'; cursor = 'cursor-not-allowed'; numberClass = 'text-red-400'; }
                else if (isToday)  { cellClass = 'border-cyan-400 ring-2 ring-cyan-300'; numberClass = 'text-cyan-600'; }

                return (
                  <div
                    key={day}
                    onClick={() => handleClick(dateStr, allBlocked, isPast)}
                    className={`border rounded-2xl transition-all duration-150 select-none ${cellClass} ${cursor} p-2 min-h-[130px] flex flex-col`}
                    title={allBlocked ? 'Aucune disponibilité' : `Réserver le ${day}/${month+1}/${year}`}
                  >
                    <div className={`text-center font-black text-base mb-2 ${numberClass}`}>{day}</div>
                    <div className="flex flex-col gap-1 flex-1">
                      {allBlocked ? (
                        <div className="flex items-center justify-center flex-1">
                          <span className="text-xs font-black text-red-400 uppercase tracking-wide text-center">Aucune<br/>dispo</span>
                        </div>
                      ) : (
                        <>
                          <SlotRow label="Mat" d={dispo.matin} />
                          <SlotRow label="Apr" d={dispo.apresmidi} />
                          <SlotRow label="Jrn" d={dispo.journee} />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-8 py-4 sm:py-5 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex flex-wrap justify-center sm:justify-start gap-3 sm:gap-5 text-xs sm:text-sm text-slate-600">
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-400 inline-block shrink-0" />Disponible</span>
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block shrink-0" />Kayaks restants</span>
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-400 inline-block shrink-0" />Complet</span>
        </div>
        <p className="text-xs sm:text-sm text-cyan-600 font-semibold italic text-center">✨ Cliquez sur un jour disponible pour réserver</p>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
